import { useRef, useState } from "preact/hooks";
import type { App } from "../app.ts";
import { cleanTags } from "../lib/fields.ts";

export function Details({ app, open }: { app: App; open: boolean }) {
  // Remount the form when the text is replaced as a whole.
  const [rev, setRev] = useState(0);
  const view = open ? app.getFrontMatterView() : null;
  const path = app.state.get().current?.path ?? "";

  return (
    <section id="details" class="popover" aria-label="Details" hidden={!open}>
      <h2 class="popover-title">Details</h2>
      {open && !view && <p class="muted">Open a file to see its details.</p>}
      {view?.kind === "none" && (
        <>
          <p class="muted">This file has no front matter.</p>
          <button
            class="button"
            type="button"
            onClick={() => {
              app.addFrontMatter();
              setRev(rev + 1);
            }}
          >
            Add Thoughts front matter
          </button>
        </>
      )}
      {view && view.kind !== "none" && (
        <Form key={`${path}:${rev}`} app={app} view={view} applied={() => setRev(rev + 1)} />
      )}
    </section>
  );
}

function Form({
  app,
  view,
  applied,
}: {
  app: App;
  view: Exclude<ReturnType<App["getFrontMatterView"]>, null | { kind: "none" }>;
  applied: () => void;
}) {
  const [error, setError] = useState("");
  const raw = useRef<HTMLTextAreaElement>(null);
  return (
    <>
      {view.kind === "thought" &&
        view.fields.map(({ field, value }) => {
          const id = `field-${field.key}`;
          return (
            <div class="form-row" key={field.key}>
              <label class="label" for={id}>
                {field.label}
              </label>
              {field.type === "tags" ? (
                <input
                  id={id}
                  class="field"
                  type="text"
                  placeholder="[tag, another tag]"
                  defaultValue={(value as string[]).join(", ")}
                  onChange={(e) =>
                    app.setField(field.key, cleanTags(e.currentTarget.value.split(",")))
                  }
                />
              ) : field.type === "date" ? (
                <input
                  id={id}
                  class="field"
                  type="date"
                  defaultValue={value as string}
                  onChange={(e) => app.setField(field.key, e.currentTarget.value)}
                />
              ) : field.type === "boolean" ? (
                <input
                  id={id}
                  type="checkbox"
                  defaultChecked={Boolean(value)}
                  onChange={(e) => app.setField(field.key, e.currentTarget.checked)}
                />
              ) : field.multiline ? (
                <textarea
                  id={id}
                  class="field"
                  rows={3}
                  placeholder={`[${field.label}]`}
                  defaultValue={value as string}
                  onChange={(e) =>
                    app.setField(field.key, e.currentTarget.value.replace(/\s*\n\s*/g, " ").trim())
                  }
                />
              ) : (
                <input
                  id={id}
                  class="field"
                  type="text"
                  placeholder={`[${field.label}]`}
                  defaultValue={value as string}
                  onChange={(e) => app.setField(field.key, e.currentTarget.value)}
                />
              )}
            </div>
          );
        })}
      {view.kind === "broken" && (
        <>
          <p class="form-error">
            The front matter is not valid YAML. Fix it below; the file is untouched until you apply.
          </p>
          {view.errors.map((e) => (
            <p class="muted small" key={e}>
              {e}
            </p>
          ))}
        </>
      )}
      {view.kind === "other" && (
        <p class="muted">This front matter is not in the Thoughts shape, so it is shown as text.</p>
      )}
      <details class="raw" open={view.kind === "broken" || view.kind === "other"}>
        <summary>Front matter as text</summary>
        <label class="visually-hidden" for="raw-yaml">
          Front matter YAML
        </label>
        <textarea
          id="raw-yaml"
          ref={raw}
          class="field mono"
          rows={6}
          spellcheck={false}
          defaultValue={view.yaml}
        />
        <p class="form-error" role="alert">
          {error}
        </p>
        <button
          class="button raw-apply"
          type="button"
          onClick={() => {
            const r = app.setRawFrontMatter((raw.current?.value ?? "").replace(/\n+$/, ""));
            if (r.ok) applied();
            else setError(r.errors[0] ?? "That is not valid YAML.");
          }}
        >
          Apply
        </button>
      </details>
    </>
  );
}
